import React, { useState } from 'react';
import { supabase } from '../lib/supabase';

/**
 * SetPassword — where an instructor chooses their own password.
 *
 * WHY it exists: onboarding used to generate a password, and Keelan texted it
 * over. That reads as unprofessional (it's *their* dashboard, not ours) and it
 * genuinely failed in the field — one instructor couldn't get in at all:
 * "Can't get logged in🙈. Very long password & can't see it when inputting",
 * then "I tried copy & paste, but it copies everything." A 16-character
 * generated string is not something you type on a phone between lessons.
 *
 * Reached two ways, both landing here via a Supabase recovery link:
 *   1. The setup link handed over at onboarding (see the API's
 *      scripts/onboard-instructor.js), sent over WhatsApp
 *   2. "Forgot password" on the login screen
 *
 * Both produce a PASSWORD_RECOVERY session, which App.js holds here until a
 * password is actually set — otherwise the recovery session would drop them
 * straight into the cockpit and the link would silently do nothing.
 */
export default function SetPassword({ email, onDone }) {
  const [password, setPassword] = useState('');
  const [confirm, setConfirm]   = useState('');
  const [show, setShow]         = useState(false);
  const [error, setError]       = useState('');
  const [saving, setSaving]     = useState(false);

  // Supabase enforces a minimum server-side too, but catching it here means
  // the instructor isn't bounced after a round trip.
  const tooShort = password.length > 0 && password.length < 8;
  const mismatch = confirm.length > 0 && password !== confirm;
  const canSubmit = password.length >= 8 && password === confirm && !saving;

  async function handleSubmit(e) {
    e.preventDefault();
    if (!canSubmit) return;
    setSaving(true);
    setError('');

    const { error } = await supabase.auth.updateUser({ password });

    if (error) {
      // The commonest real failure is an expired link: recovery tokens are
      // short-lived, and an instructor teaching all day may well open it hours
      // later. Say so plainly instead of showing a raw auth error.
      const expired = /expired|invalid|not found/i.test(error.message);
      setError(expired
        ? 'That setup link has expired. Head back to the sign-in page and tap "Forgot password" to get a fresh one.'
        : error.message);
      setSaving(false);
      return;
    }

    onDone();
  }

  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      minHeight: '100vh', background: '#0f0f0f', fontFamily: 'system-ui, sans-serif',
      padding: 16, boxSizing: 'border-box'
    }}>
      <div style={{
        background: '#1a1a1a', border: '1px solid #2a2a2a', borderRadius: 12,
        padding: 40, width: '100%', maxWidth: 380
      }}>
        <div style={{ textAlign: 'center', marginBottom: 28 }}>
          <div style={{ fontSize: 28, fontWeight: 700, color: '#fff', marginBottom: 4 }}>
            Visionry
          </div>
          <div style={{ fontSize: 14, color: '#888' }}>Choose your password</div>
          {email && (
            <div style={{ fontSize: 12, color: '#666', marginTop: 8 }}>{email}</div>
          )}
        </div>

        <form onSubmit={handleSubmit}>
          <Field
            label="New password"
            value={password}
            onChange={setPassword}
            show={show}
            autoComplete="new-password"
            hint={tooShort ? 'At least 8 characters' : null}
          />

          <Field
            label="Confirm password"
            value={confirm}
            onChange={setConfirm}
            show={show}
            autoComplete="new-password"
            hint={mismatch ? "Those don't match" : null}
          />

          {/* Typing a password blind on a phone is what broke this last time. */}
          <label style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 20,
            fontSize: 13, color: '#888', cursor: 'pointer'
          }}>
            <input type="checkbox" checked={show} onChange={e => setShow(e.target.checked)} />
            Show password
          </label>

          {error && (
            <div style={{
              background: '#2a1a1a', border: '1px solid #ff4444', borderRadius: 8,
              padding: '10px 12px', color: '#ff6666', fontSize: 13, marginBottom: 16
            }}>
              {error}
            </div>
          )}

          <button
            type="submit"
            disabled={!canSubmit}
            style={{
              width: '100%', padding: 12, background: '#6c47ff', border: 'none',
              borderRadius: 8, color: '#fff', fontSize: 15, fontWeight: 600,
              cursor: canSubmit ? 'pointer' : 'not-allowed',
              opacity: canSubmit ? 1 : 0.5
            }}
          >
            {saving ? 'Saving…' : 'Save and continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

function Field({ label, value, onChange, show, hint, autoComplete }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: 'block', fontSize: 13, color: '#888', marginBottom: 6 }}>
        {label}
      </label>
      <input
        type={show ? 'text' : 'password'}
        value={value}
        onChange={e => onChange(e.target.value)}
        autoComplete={autoComplete}
        required
        style={{
          width: '100%', padding: '10px 12px', background: '#0f0f0f',
          border: `1px solid ${hint ? '#ff4444' : '#2a2a2a'}`, borderRadius: 8,
          color: '#fff', fontSize: 14, outline: 'none', boxSizing: 'border-box'
        }}
      />
      {hint && <div style={{ fontSize: 12, color: '#ff6666', marginTop: 5 }}>{hint}</div>}
    </div>
  );
}
